'use client';

import { useApi } from '@repo/api';
import { useAuth } from '@repo/auth';
import { useOrganisationScope } from '@repo/features/organisations';
import { useAppForm, useSelector } from '@repo/form';
import { useMutation, useQueryClient } from '@repo/tanstack/query';
import { ApiErrors } from '@repo/features/error';
import { Button, Separator } from '@repo/ui/components';
import { isResponseError } from '@repo/upfetch';
import { useState } from 'react';
import { normalizeSource } from '@/app/@protected/_components/create-company/helpers';
import { useCreateCompanySheet } from './context';
import { ExistingCompanyAlert } from './existing-company-alert';
import { buildAddress, emptyToUndefined, normalizeWebsite } from './helpers';
import { createCompanyDefaultValues, CreateCompanySchema } from './schema';
import { useCreateCompanyVat } from './use-create-company-vat';
import { toCountryCode } from '@repo/sdk/helpers/phone';
import { PhoneField } from '@repo/form/phone';
export const CreateCompanyForm = () => {
  const api = useApi();
  const {
    source
  } = useAuth();
  const {
    scope
  } = useOrganisationScope();
  const queryClient = useQueryClient();
  const {
    closeCreateCompanySheet,
    options
  } = useCreateCompanySheet();
  const [submitErrorMessage, setSubmitErrorMessage] = useState<null | string>(null);
  const isAllOrganisationsSelected = scope.kind === 'all';
  const {
    isPending: isCreatingBroker,
    mutateAsync: createBroker
  } = useMutation(api.companies.createBroker.mutationOptions());
  const {
    isPending: isCreatingShipper,
    mutateAsync: createShipper
  } = useMutation(api.companies.createShipper.mutationOptions());
  const {
    isPending: isCreatingHaulier,
    mutateAsync: createHaulier
  } = useMutation(api.hauliers.company.create.mutationOptions());
  const defaultValues = {
    ...createCompanyDefaultValues,
    ...options?.initialValues,
    address: {
      ...createCompanyDefaultValues.address,
      ...options?.initialValues?.address
    },
    organisation_id: options?.inheritedOrganisationId ?? options?.initialValues?.organisation_id ?? null,
    type: options?.defaultType ?? options?.initialValues?.type ?? createCompanyDefaultValues.type
  };
  const isPending = isCreatingBroker || isCreatingShipper || isCreatingHaulier;
  const form = useAppForm({
    defaultValues,
    onSubmit: async ({
      value
    }) => {
      setSubmitErrorMessage(null);
      if (value.type !== 'broker' && value.type !== 'shipper' && value.type !== 'haulier') {
        return;
      }
      const address = buildAddress({
        ...value.address,
        country: value.country
      });
      const payload = {
        address,
        email: emptyToUndefined(value.email),
        notes: emptyToUndefined(value.notes),
        organisation_id: value.organisation_id ?? undefined,
        phone: emptyToUndefined(value.phone),
        phone_country: emptyToUndefined(value.phone_country),
        registration_number: emptyToUndefined(value.registration_number),
        source,
        title: value.title.trim(),
        vat: emptyToUndefined(value.vat),
        website: normalizeWebsite(value.website)
      };
      try {
        const company = value.type === 'broker' ? await createBroker({
          ...payload,
          country: emptyToUndefined(value.country),
          type: 'broker'
        }) : value.type === 'shipper' ? await createShipper({
          ...payload,
          type: 'shipper'
        }) : await createHaulier({
          ...payload,
          country: emptyToUndefined(value.country),
          source: normalizeSource(options?.source ?? source),
          type: 'haulier'
        });
        await queryClient.invalidateQueries({
          predicate: query => query.queryKey[0] === 'companies'
        });
        options?.onCreated?.({
          id: company.id,
          title: company.title,
          type: value.type
        });
        closeCreateCompanySheet();
      } catch (error) {
        if (isResponseError(error)) {
          setSubmitErrorMessage(error.data.message);
          return;
        }
        throw error;
      }
    },
    validators: {
      onSubmit: CreateCompanySchema
    }
  });
  const {
    AppField,
    AppForm,
    handleSubmit
  } = form;
  const country = useSelector(form.store, state => state.values.country);
  const organisationId = useSelector(form.store, state_0 => state_0.values.organisation_id);
  const vat = useSelector(form.store, state_1 => state_1.values.vat);
  const {
    existingCompany,
    handleVatBlur,
    isVatLookupPending,
    vatLookupMessage
  } = useCreateCompanyVat({
    country,
    organisationId,
    setFieldValue: form.setFieldValue,
    vat
  });
  const allowedTypes = options?.allowedTypes ?? ['shipper', 'broker'];
  const isSubmitDisabled = isPending || isVatLookupPending || Boolean(existingCompany);
  const existingCompanyTypeAlias = existingCompany?.type?.alias;
  const existingCompanyType = existingCompanyTypeAlias === 'broker' ? 'broker' : existingCompanyTypeAlias === 'shipper' ? 'shipper' : existingCompanyTypeAlias === 'haulier' ? 'haulier' : null;
  return <AppForm data-sentry-element="AppForm" data-sentry-component="CreateCompanyForm" data-sentry-source-file="form.tsx">
      <form className="flex flex-col gap-8" onSubmit={event => {
      event.preventDefault();
      event.stopPropagation();
    }}>
        <div className="flex flex-col gap-6">
          <h2 className="text-2xl font-bold">Business data</h2>
          <AppField name="organisation_id" data-sentry-element="AppField" data-sentry-source-file="form.tsx">
            {field => <field.FormOrganisationSelect disabled={Boolean(options?.inheritedOrganisationId) || !isAllOrganisationsSelected || isPending} label="Organisation *" />}
          </AppField>
        </div>
        <Separator data-sentry-element="Separator" data-sentry-source-file="form.tsx" />

        <div className="flex flex-col gap-6">
          <h2 className="text-2xl font-bold">Company settings</h2>
          <AppField name="type" data-sentry-element="AppField" data-sentry-source-file="form.tsx">
            {field_0 => <field_0.FormCompanyTypeSelect allowedValues={options?.allowedTypes ?? ['shipper', 'broker']} isPending={isPending} label="Company type *" placeholder="Select type" required />}
          </AppField>

          <AppField name="country" data-sentry-element="AppField" data-sentry-source-file="form.tsx">
            {field_1 => <field_1.FormCountrySelect display="name" isPending={isPending} label="Country *" required variant="outline" />}
          </AppField>
        </div>

        <Separator data-sentry-element="Separator" data-sentry-source-file="form.tsx" />

        <div className="flex flex-col gap-6">
          <h2 className="text-2xl font-bold">Company details</h2>
          <AppField name="vat" data-sentry-element="AppField" data-sentry-source-file="form.tsx">
            {field_2 => <field_2.Input description="Enter the VAT number to auto-fill the remaining fields (verified via Creditsafe)" disabled={isPending || isVatLookupPending} label="Vat No" onBlur={() => void handleVatBlur(field_2.state.value)} placeholder="GB123456789" />}
          </AppField>

          {vatLookupMessage ? <p className="text-sm text-zinc-500">{vatLookupMessage}</p> : null}

          <AppField name="title" data-sentry-element="AppField" data-sentry-source-file="form.tsx">
            {field_3 => <field_3.Input disabled={isPending || isVatLookupPending} label="Legal name *" placeholder="Acme Ltd" required />}
          </AppField>

          <AppField name="address.address" data-sentry-element="AppField" data-sentry-source-file="form.tsx">
            {field_4 => <field_4.Input disabled={isPending} label="Address - 1 line" placeholder="Street address" />}
          </AppField>

          <AppField name="address.address_second_line" data-sentry-element="AppField" data-sentry-source-file="form.tsx">
            {field_5 => <field_5.Input disabled={isPending} label="Address - 2 line" placeholder="Building, floor, unit" />}
          </AppField>

          <AppField name="address.postcode" data-sentry-element="AppField" data-sentry-source-file="form.tsx">
            {field_6 => <field_6.Input disabled={isPending} label="Post code" placeholder="AB1 2CD" />}
          </AppField>

          <AppField name="address.city" data-sentry-element="AppField" data-sentry-source-file="form.tsx">
            {field_7 => <field_7.Input disabled={isPending} label="City" placeholder="London" />}
          </AppField>

          <AppField name="registration_number" data-sentry-element="AppField" data-sentry-source-file="form.tsx">
            {field_8 => <field_8.Input disabled={isPending} label="Company registration number (CRN)" placeholder="123456789" />}
          </AppField>

          <AppField name="notes" data-sentry-element="AppField" data-sentry-source-file="form.tsx">
            {field_9 => <field_9.Textarea disabled={isPending} label="Notes" placeholder="Your notes about company" rows={4} />}
          </AppField>
        </div>

        <Separator data-sentry-element="Separator" data-sentry-source-file="form.tsx" />

        <div className="flex flex-col gap-6">
          <h2 className="text-2xl font-bold">Company contact Info</h2>
          <AppField name="email" data-sentry-element="AppField" data-sentry-source-file="form.tsx">
            {field_10 => <field_10.Input disabled={isPending} label="Email" placeholder="office@acme.co.uk" type="email" />}
          </AppField>

          <PhoneField defaultCountry={toCountryCode(country)} disabled={isPending} fields={{
          phone: 'phone',
          phone_country: 'phone_country'
        }} form={form} label="Phone" syncCountryWithDefault variant={'button-group'} data-sentry-element="PhoneField" data-sentry-source-file="form.tsx" />

          <AppField name="website" data-sentry-element="AppField" data-sentry-source-file="form.tsx">
            {field_11 => <field_11.Input disabled={isPending} label="Website (company domain)" placeholder="http://acme.co.uk" />}
          </AppField>
        </div>

        {existingCompany ? <ExistingCompanyAlert allowedInsertTypes={allowedTypes} company={existingCompany} onInsert={existingCompanyType && allowedTypes.includes(existingCompanyType) ? () => {
        options?.onCreated?.({
          id: existingCompany.id,
          title: existingCompany.title,
          type: existingCompanyType
        });
        closeCreateCompanySheet();
      } : null} /> : null}

        <ApiErrors errors={submitErrorMessage} data-sentry-element="ApiErrors" data-sentry-source-file="form.tsx" />

        <div className="flex items-center justify-between gap-3">
          <Button onClick={closeCreateCompanySheet} type="button" variant="outline" data-sentry-element="Button" data-sentry-source-file="form.tsx">
            Cancel
          </Button>
          <Button disabled={isSubmitDisabled} onClick={handleSubmit} type="submit" data-sentry-element="Button" data-sentry-source-file="form.tsx">
            Create a new company
          </Button>
        </div>
      </form>
    </AppForm>;
};